import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { weekDaysOptions } from 'Shared/constants/constants';
import { setWorkspaceRecurrence, deleteRecurrenceInWorkspace } from 'Shared/actions/workspaces';
import TimeInput from 'Shared/components/TimeInput/TimeInput';
import CustomSingleDatePicker from 'Shared/components/CustomSingleDatePicker/CustomSingleDatePicker';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpButton from 'Wpds/components/button/WpButton';

import './WorkspaceRecurrenceModal.scss';

type Props = {
  workspaceId: string;
  onToggle(): void;
};

const WorkspaceRecurrenceModal = ({ onToggle, workspaceId }: Props) => {
  const dispatch = useDispatch();
  const workspaceRecurrence = useDeepEqualSelector(state => state.workspace.recurrence);

  const dayTodayNumber = new Date().getDay();
  const dayToday = weekDaysOptions.find(({ dayNumber }) => dayNumber === dayTodayNumber).value;
  const initialDayValue = workspaceRecurrence ? workspaceRecurrence.dayOfWeek : dayToday;

  const [intervalAmount, setIntervalAmount] = useState(workspaceRecurrence ? workspaceRecurrence.intervalAmount : 1);
  const [day, setDay] = useState(initialDayValue);
  const [timeOfDay, setTimeOfDay] = useState(workspaceRecurrence ? workspaceRecurrence.timeOfDay : '09:00');
  const [startsAt, setStartsAt] = useState(workspaceRecurrence ? moment(workspaceRecurrence.startsAt) : moment());
  const [saving, setSaving] = useState(false);

  const handleIntervalAmountChange = event => setIntervalAmount(parseInt(event.target.value || '1', 10));

  const handleTimeChange = value => setTimeOfDay(value || '09:00');

  const handleStartsAtChange = momentDate => {
    const momentDay = weekDaysOptions.find(({ dayNumber }) => dayNumber === momentDate.day()).value;
    setDay(momentDay);
    setStartsAt(momentDate);
  };

  const deleteWorkspaceRecurrence = async () => {
    setSaving(true);

    await dispatch(
      deleteRecurrenceInWorkspace({
        workspaceId,
      })
    ).finally(() => {
      setSaving(false);
    });

    onToggle();
  };

  const saveWorkspaceRecurrence = async () => {
    setSaving(true);

    await dispatch(
      setWorkspaceRecurrence({
        workspaceId,
        recurrence: {
          at: `${day} ${timeOfDay}`,
          intervalAmount,
          intervalUnit: 'week',
          startsAt: moment(startsAt).format(),
        },
      })
    ).finally(() => {
      setSaving(false);
    });

    onToggle();
  };

  const isDisabledSubmit = !intervalAmount;

  return (
    <WpModal size="sm" isOpen onToggle={onToggle} zIndex={1100}>
      <WpModalHeader>Set Meeting Time</WpModalHeader>
      <WpModalBody>
        <div className="WorkspaceRecurrenceModal">
          <p>
            Set the meeting time to get reminders for upcoming meetings & view your upcoming discussion topics in your
            Home.
          </p>
          <div className="WorkspaceRecurrenceModal__start-at d-flex align-items-center mb-3">
            <span>Start Date</span>
            <CustomSingleDatePicker date={startsAt} onDateChange={handleStartsAtChange} withPortal />
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <span>Repeat every</span>
            <input
              className="WorkspaceRecurrenceModal__recurrence-amount  pl-1 py-1 mx-2"
              type="number"
              name="recurrenceAmount"
              min="1"
              step="1"
              value={intervalAmount}
              onChange={handleIntervalAmountChange}
            />
            <span>{intervalAmount > 1 ? 'weeks' : 'week'}</span>
            <div className="d-flex align-items-center">
              <span>&nbsp;at&nbsp;&nbsp;</span>
              <TimeInput
                className="WorkspaceRecurrenceModal__time-input form-control time-of-day-input"
                value={timeOfDay}
                title="Repeat at"
                required
                onChange={handleTimeChange}
              />
            </div>
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        {workspaceRecurrence && (
          <div className="mr-auto">
            <WpButton disabled={saving} color="danger" onClick={deleteWorkspaceRecurrence}>
              Remove time
            </WpButton>
          </div>
        )}
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle} disabled={saving}>
            Cancel
          </WpButton>
          <WpButton
            loading={saving}
            disabled={isDisabledSubmit}
            onClick={saveWorkspaceRecurrence}
            qaTest="WorkspaceRecurrenceModal-save-btn"
          >
            Save
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default WorkspaceRecurrenceModal;
